export const SUPPORT_PROVIDERS = ["venmo", "paypal", "zelle", "cashapp"] as const

export type SupportProvider = (typeof SUPPORT_PROVIDERS)[number]

export type NormalizedEventLink = {
  id: string
  label: string
  normalizedUrl: string
}

export type NormalizedSupportMethod = {
  id: string
  provider: SupportProvider
  value: string
  normalizedUrl?: string
  copyValue: string
  displayValue: string
}

const DISALLOWED_PROTOCOLS = new Set([
  "javascript:",
  "data:",
  "file:",
  "vbscript:",
  "blob:",
  "about:",
  "chrome:",
  "chrome-extension:",
])

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_DIGIT_RE = /^[+]?[\d()\-\s.]{7,24}$/
const VENMO_HANDLE_RE = /^[A-Za-z0-9_]{1,30}$/
const CASHAPP_HANDLE_RE = /^[A-Za-z0-9_]{1,20}$/
const PAYPAL_HANDLE_RE = /^[A-Za-z0-9._-]{1,127}$/

function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/\.+$/, "")
}

function canonicalizeHttpUrl(url: URL): string {
  const protocol = url.protocol.toLowerCase()
  const host = normalizeHost(url.hostname)
  const port = url.port
  const auth = url.username || url.password ? `${url.username}${url.password ? `:${url.password}` : ""}@` : ""
  const path = url.pathname || "/"
  const query = url.search
  const hash = ""
  const effectivePort =
    (protocol === "http:" && port === "80") || (protocol === "https:" && port === "443")
      ? ""
      : port
  return `${protocol}//${auth}${host}${effectivePort ? `:${effectivePort}` : ""}${path}${query}${hash}`
}

export function isSafeHttpUrl(input: string): boolean {
  const trimmed = input.trim()
  if (!trimmed) return false
  try {
    const parsed = new URL(trimmed)
    if (DISALLOWED_PROTOCOLS.has(parsed.protocol.toLowerCase())) return false
    return parsed.protocol === "http:" || parsed.protocol === "https:"
  } catch {
    return false
  }
}

export function normalizeExternalUrl(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  try {
    const parsed = new URL(trimmed)
    if (DISALLOWED_PROTOCOLS.has(parsed.protocol.toLowerCase())) return null
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null
    if (!parsed.hostname.trim()) return null
    return canonicalizeHttpUrl(parsed)
  } catch {
    return null
  }
}

export function validateExternalUrl(input: string): { ok: true; normalizedUrl: string } | { ok: false } {
  const normalizedUrl = normalizeExternalUrl(input)
  if (!normalizedUrl) return { ok: false }
  return { ok: true, normalizedUrl }
}

function parseUrl(input: string): URL | null {
  try {
    return new URL(input.trim())
  } catch {
    return null
  }
}

function normalizeVenmoValue(raw: string): { value: string; openUrl: string; copyValue: string; displayValue: string } | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  let handle = trimmed.replace(/^@+/, "")
  if (isSafeHttpUrl(trimmed)) {
    const parsed = parseUrl(trimmed)
    if (!parsed) return null
    const host = normalizeHost(parsed.hostname)
    if (!host.endsWith("venmo.com")) return null
    const pathBits = parsed.pathname.split("/").filter(Boolean)
    const candidate = pathBits[pathBits[0] === "u" ? 1 : 0]
    if (!candidate) return null
    handle = decodeURIComponent(candidate).replace(/^@+/, "")
  }
  if (!VENMO_HANDLE_RE.test(handle)) return null
  return {
    value: handle,
    openUrl: `https://venmo.com/u/${handle}`,
    copyValue: `@${handle}`,
    displayValue: `@${handle}`,
  }
}

function normalizeCashAppValue(raw: string): { value: string; openUrl: string; copyValue: string; displayValue: string } | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  let handle = trimmed.replace(/^\$+/, "").replace(/^@+/, "")
  if (isSafeHttpUrl(trimmed)) {
    const parsed = parseUrl(trimmed)
    if (!parsed) return null
    const host = normalizeHost(parsed.hostname)
    if (host !== "cash.app" && host !== "www.cash.app") return null
    const pathBits = parsed.pathname.split("/").filter(Boolean)
    const candidate = pathBits[0]
    if (!candidate) return null
    handle = decodeURIComponent(candidate).replace(/^\$+/, "").replace(/^@+/, "")
  }
  if (!CASHAPP_HANDLE_RE.test(handle)) return null
  return {
    value: handle,
    openUrl: `https://cash.app/$${handle}`,
    copyValue: `$${handle}`,
    displayValue: `$${handle}`,
  }
}

function normalizePaypalValue(raw: string): { value: string; openUrl?: string; copyValue: string; displayValue: string } | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (EMAIL_RE.test(trimmed.toLowerCase())) {
    const email = trimmed.toLowerCase()
    return {
      value: email,
      copyValue: email,
      displayValue: email,
    }
  }
  let handle = trimmed.replace(/^@+/, "")
  const paypalUrlCandidate =
    isSafeHttpUrl(trimmed) ? trimmed : /^paypal\.me\//i.test(trimmed) ? `https://${trimmed}` : trimmed
  if (isSafeHttpUrl(paypalUrlCandidate)) {
    const parsed = parseUrl(paypalUrlCandidate)
    if (!parsed) return null
    const host = normalizeHost(parsed.hostname)
    if (host !== "paypal.me" && host !== "www.paypal.me") return null
    const pathBits = parsed.pathname.split("/").filter(Boolean)
    if (!pathBits[0]) return null
    handle = decodeURIComponent(pathBits[0]).replace(/^@+/, "")
  }
  if (!PAYPAL_HANDLE_RE.test(handle)) return null
  return {
    value: handle,
    openUrl: `https://paypal.me/${handle}`,
    copyValue: `@${handle}`,
    displayValue: `@${handle}`,
  }
}

function normalizeZelleValue(raw: string): { value: string; copyValue: string; displayValue: string } | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (isSafeHttpUrl(trimmed)) return null
  if (EMAIL_RE.test(trimmed.toLowerCase())) {
    const email = trimmed.toLowerCase()
    return { value: email, copyValue: email, displayValue: email }
  }
  if (!PHONE_DIGIT_RE.test(trimmed)) return null
  const digits = trimmed.replace(/[^\d+]/g, "")
  if (digits.length < 7) return null
  return { value: digits, copyValue: digits, displayValue: digits }
}

export function normalizeSupportMethod(
  provider: string,
  value: string,
): Omit<NormalizedSupportMethod, "id"> | null {
  const normalizedProvider = provider.trim().toLowerCase() as SupportProvider
  if (!SUPPORT_PROVIDERS.includes(normalizedProvider)) return null
  if (normalizedProvider === "venmo") {
    const parsed = normalizeVenmoValue(value)
    if (!parsed) return null
    return {
      provider: normalizedProvider,
      value: parsed.value,
      normalizedUrl: parsed.openUrl,
      copyValue: parsed.copyValue,
      displayValue: parsed.displayValue,
    }
  }
  if (normalizedProvider === "paypal") {
    const parsed = normalizePaypalValue(value)
    if (!parsed) return null
    return {
      provider: normalizedProvider,
      value: parsed.value,
      normalizedUrl: parsed.openUrl,
      copyValue: parsed.copyValue,
      displayValue: parsed.displayValue,
    }
  }
  if (normalizedProvider === "zelle") {
    const parsed = normalizeZelleValue(value)
    if (!parsed) return null
    return {
      provider: normalizedProvider,
      value: parsed.value,
      copyValue: parsed.copyValue,
      displayValue: parsed.displayValue,
    }
  }
  const parsed = normalizeCashAppValue(value)
  if (!parsed) return null
  return {
    provider: normalizedProvider,
    value: parsed.value,
    normalizedUrl: parsed.openUrl,
    copyValue: parsed.copyValue,
    displayValue: parsed.displayValue,
  }
}

export function parseSupportMethod(raw: unknown): NormalizedSupportMethod | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as Record<string, unknown>
  const id = typeof item.id === "string" ? item.id.trim() : ""
  const providerRaw =
    typeof item.provider === "string"
      ? item.provider
      : typeof item.type === "string"
        ? item.type
        : ""
  const valueRaw =
    typeof item.value === "string"
      ? item.value
      : typeof item.handle === "string"
        ? item.handle
        : ""
  if (!id || !providerRaw || !valueRaw) return null
  const normalized = normalizeSupportMethod(providerRaw, valueRaw)
  if (!normalized) return null
  return { id, ...normalized }
}

export function getSupportMethodDisplayValue(item: Pick<NormalizedSupportMethod, "displayValue">): string {
  return item.displayValue
}

export function getSupportMethodCopyValue(item: Pick<NormalizedSupportMethod, "copyValue">): string {
  return item.copyValue
}

export function getSupportMethodOpenUrl(item: Pick<NormalizedSupportMethod, "normalizedUrl">): string | null {
  const url = item.normalizedUrl?.trim()
  if (!url) return null
  return isSafeHttpUrl(url) ? url : null
}

export function parseEventLink(raw: unknown): NormalizedEventLink | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as Record<string, unknown>
  const id = typeof item.id === "string" ? item.id.trim() : ""
  const label = typeof item.label === "string" ? item.label.trim() : ""
  const srcUrl =
    typeof item.normalizedUrl === "string"
      ? item.normalizedUrl
      : typeof item.url === "string"
        ? item.url
        : ""
  const out = normalizeExternalUrl(srcUrl)
  if (!id || !label || !out) return null
  return { id, label, normalizedUrl: out }
}
